import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import {
  Settings as SettingsIcon,
  Link as LinkIcon,
  Globe,
  Bell,
  Megaphone,
  LifeBuoy,
  CreditCard,
  Calendar as CalIcon,
  FileText,
} from "lucide-react";
import { logout } from "../store/authSlice";
import { useLang } from "../i18n/LanguageProvider";
import GlobalDashboardSidebar from "./admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "./admin/useGlobalSidebar";
import QuickLinks from "../mainframes/sidebar/QuickLinks";
import LanguageSetting from "../mainframes/sidebar/LanguageSetting";
import SupportTickets from "../mainframes/sidebar/SupportTickets";
import ReportScheduler from "../mainframes/sidebar/ReportScheduler";
import Announcements from "../mainframes/sidebar/Announcements";
import GeneralSetting from "../mainframes/sidebar/GeneralSettings";
import RechargePlans from "../mainframes/sidebar/RechargePlans";
import NotificationList from "../mainframes/sidebar/NotificationList";
import CalendarManager from "../mainframes/sidebar/CalendarManager";
import RenewalRequest from "../mainframes/sidebar/RenewalRequest";
import { USER_ROLE_ROUTE_CONFIG } from "./users/userRoleConfig";
import "./admin/dashboard-redesign.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

export default function Profile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLang();
  const user = useSelector((state) =>
    state.auth?.user ||
    state.user?.data ||
    state.auth?.currentUser ||
    state.user?.currentUser ||
    state.auth?.userInfo
  );

  const roleRaw = user?.role || "";
  const role = String(roleRaw).trim().toLowerCase();
  const profileName = user?.name || user?.username || "User";
  const roleLabel = String(roleRaw || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const canSeeAll = useMemo(
    () => ["admin", "manager", "sub_admin", "group_admin"].includes(role),
    [role]
  );
  const hideForUser = useMemo(() => role === "user", [role]);
  const hideForViewer = useMemo(() => role === "viewer", [role]);
  const isFieldEmployee = useMemo(() => role === "field_emp", [role]);
  const canSeeRenewalRequest = useMemo(() => ["admin", "manager", "sub_admin", "group_admin"].includes(role), [role]);

  const settingsTabs = useMemo(
    () => [
      { id: "general", label: t("GENERAL_SETTINGS", "General Settings"), icon: <SettingsIcon size={16} /> },
      { id: "links", label: t("QUICK_LINKS", "Quick Links"), icon: <LinkIcon size={16} /> },
      { id: "language", label: t("LANGUAGE_SETTINGS", "Language Settings"), icon: <Globe size={16} /> },
      { id: "notifications", label: t("NOTIFICATION_SETTINGS", "Notification Settings"), icon: <Bell size={16} /> },
      { id: "announcements", label: t("ANNOUNCEMENTS", "Announcements"), icon: <Megaphone size={16} /> },
      { id: "support", label: t("SUPPORT_TICKETS", "Support Tickets"), icon: <LifeBuoy size={16} /> },
      { id: "plans", label: t("RECHARGE_PLANS", "Recharge Plans"), icon: <CreditCard size={16} /> },
      { id: "calendar", label: t("CALENDAR", "Calendar"), icon: <CalIcon size={16} /> },
      { id: "renewalRequest", label: t("RENEWAL_REQUEST", "Renewal Requests"), icon: <FileText size={16} /> },
      { id: "reports", label: t("REPORT_SCHEDULER", "Report Scheduler"), icon: <FileText size={16} /> },
    ],
    [t]
  );

  const menuItems = useMemo(() => {
    if (isFieldEmployee) return settingsTabs.filter((m) => m.id === "general");
    let allowed = settingsTabs;
    if (canSeeAll) allowed = settingsTabs;
    else if (hideForViewer) allowed = settingsTabs.filter((m) => !["plans", "reports", "renewalRequest"].includes(m.id));
    else if (hideForUser) allowed = settingsTabs.filter((m) => !["plans", "renewalRequest"].includes(m.id));
    if (!canSeeRenewalRequest) allowed = allowed.filter((m) => m.id !== "renewalRequest");
    return allowed;
  }, [canSeeAll, canSeeRenewalRequest, hideForUser, hideForViewer, isFieldEmployee, settingsTabs]);
  const activeTab = useMemo(() => {
    const requested = String(searchParams.get("section") || "general")
      .trim()
      .toLowerCase();
    const matchedTab = menuItems.find(
      (item) => String(item.id || "").toLowerCase() === requested
    );
    return matchedTab?.id || menuItems[0]?.id || "general";
  }, [menuItems, searchParams]);

  useEffect(() => {
    const requested = String(searchParams.get("section") || "").trim().toLowerCase();
    if (requested === String(activeTab || "").toLowerCase()) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", activeTab);
      return next;
    }, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  const globalSidebarItemsLegacy = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <AssessmentOutlinedIcon fontSize="small" />,
      active: location.pathname === "/dashboard",
      onClick: () => navigate("/dashboard"),
    },
    {
      key: "vehicles",
      label: "Vehicles",
      icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
      active: false,
      onClick: () => navigate("/dashboard?tab=devices"),
    },
    {
      key: "live-tracking",
      label: "Live Tracking",
      icon: <PlaceOutlinedIcon fontSize="small" />,
      active: false,
      onClick: () => navigate("/live-map"),
    },
    {
      key: "trips",
      label: "Trips",
      icon: <RouteOutlinedIcon fontSize="small" />,
      active: location.pathname === "/details",
      onClick: () => navigate("/logistic/trips"),
    },
    {
      key: "geofences",
      label: "Geofences",
      icon: <PlaceOutlinedIcon fontSize="small" />,
      active: location.pathname === "/geofences",
      onClick: () => navigate("/geofences"),
    },
    {
      key: "notifications",
      label: "Notifications",
      icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
      active: location.pathname === "/notifications",
      onClick: () => navigate("/notifications"),
    },
    {
      key: "reports",
      label: "Reports",
      icon: <ReceiptLongOutlinedIcon fontSize="small" />,
      active: location.pathname === "/reports",
      onClick: () => navigate("/reports"),
    },
    {
      key: "tasks",
      label: "Tasks",
      icon: <TaskAltOutlinedIcon fontSize="small" />,
      active: location.pathname.startsWith("/tasks"),
      children: TASK_MENU_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === `/tasks/${item.key}`,
        onClick: () => navigate(`/tasks/${item.key}`),
      })),
    },
    {
      key: "messages",
      label: "Messages",
      icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
      active: location.pathname === "/message",
      children: MESSAGE_MENU_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
        onClick: () => navigate(`/message?section=${item.key}`),
      })),
    },
    {
      key: "logistic",
      label: "Logistic",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      active: location.pathname.startsWith("/logistic"),
      children: [
        { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
        { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
        { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
        { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
      ],
    },
    {
      key: "organization",
      label: "Groups",
      icon: <Groups2OutlinedIcon fontSize="small" />,
      active: location.pathname.startsWith("/groups"),
      onClick: () => navigate("/groups"),
    },
    {
      key: "reminders",
      label: "Reminders",
      icon: <EventNoteOutlinedIcon fontSize="small" />,
      active: location.pathname === "/reminders",
      onClick: () => navigate("/reminders"),
    },
    {
      key: "routes",
      label: "Routes",
      icon: <RouteOutlinedIcon fontSize="small" />,
      active: location.pathname === "/routes",
      onClick: () => navigate("/routes"),
    },
    {
      key: "expense",
      label: "Expense",
      icon: <PaidOutlinedIcon fontSize="small" />,
      active: location.pathname.startsWith("/expense"),
      children: EXPENSE_MENU_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === item.path,
        onClick: () => navigate(item.path),
      })),
    },
    {
      key: "sensors",
      label: "Sensors",
      icon: <SensorsRoundedIcon fontSize="small" />,
      active: location.pathname.startsWith("/sensors"),
      children: SENSOR_MENU_ITEMS.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === item.path,
        onClick: () => navigate(item.path),
      })),
    },
    {
      key: "users",
      label: "Users & Roles",
      icon: <PersonOutlineRoundedIcon fontSize="small" />,
      active: location.pathname.startsWith("/users"),
      children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
        key: item.key,
        label: item.label,
        active: location.pathname === item.path,
        onClick: () => navigate(item.path),
      })),
    },
    {
      key: "billing",
      label: "Billing & Licenses",
      icon: <AssessmentOutlinedIcon fontSize="small" />,
      active: location.pathname === "/licenses",
      children: [
        { key: "licenses", label: "Licenses", active: location.pathname === "/licenses" && !location.search.includes("section="), onClick: () => navigate("/licenses") },
        { key: "all-licenses", label: "All Licenses", active: location.pathname === "/licenses" && location.search.includes("section=all-licenses"), onClick: () => navigate("/licenses?section=all-licenses") },
        { key: "purchase-license", label: "Purchase License", active: location.pathname === "/licenses" && location.search.includes("section=purchase-license"), onClick: () => navigate("/licenses?section=purchase-license") },
        { key: "license-summary", label: "License Summary", active: location.pathname === "/licenses" && location.search.includes("section=license-summary"), onClick: () => navigate("/licenses?section=license-summary") },
        { key: "transactions", label: "Transactions", active: location.pathname === "/licenses" && location.search.includes("section=transactions"), onClick: () => navigate("/licenses?section=transactions") },
      ],
    },
    {
      key: "settings",
      label: "Settings",
      icon: <SettingsOutlinedIcon fontSize="small" />,
      active: location.pathname === "/profile",
      onClick: () => navigate("/profile"),
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "general":
        return <GeneralSetting />;
      case "links":
        return <QuickLinks />;
      case "language":
        return <LanguageSetting />;
      case "notifications":
        return <NotificationList />;
      case "announcements":
        return <Announcements />;
      case "support":
        return <SupportTickets />;
      case "calendar":
        return <CalendarManager />;
      case "renewalRequest":
        return <RenewalRequest />;
      case "reports":
        return <ReportScheduler />;
      case "plans":
        return <RechargePlans />;
      default:
        return null;
    }
  };

  const globalSidebarItems = useGlobalSidebarItems();
  void globalSidebarItemsLegacy;

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={globalSidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main
        className="dashboard-content"
        style={{
          gap: 24,
          width: "100%",
          alignSelf: "stretch",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "2.4rem" },
              fontWeight: 900,
              color: "#12233d",
              lineHeight: 1.1,
            }}
          >
            My Profile
          </Typography>
          <Typography sx={{ mt: 1, color: "#5f7089", fontSize: "1rem" }}>
            Manage your personal information and account preferences
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.78)",
            border: "1px solid rgba(203,213,225,0.7)",
            color: "#5f7089",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          Current section: {menuItems.find((item) => item.id === activeTab)?.label || "General Settings"}
        </Box>

        <Box sx={{ minWidth: 0 }}>{renderContent()}</Box>
      </main>
    </div>
  );
}
